import { ArrowUpRight, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useApp } from "../context/AppContext";
import { ADDRESS, EMAIL, EXTRA_PHONES, INSTAGRAM_HANDLE, INSTAGRAM_URL, MAPS_URL, WA_DISPLAY } from "../lib/site";
import { Reveal, SectionHead } from "./Reveal";

export const Contact = () => {
  const { t, waLink, lang } = useApp();
  const rows = [
    {
      key: "whatsapp",
      icon: MessageCircle,
      label: t.contact.whatsappLabel,
      value: WA_DISPLAY,
      note: t.contact.whatsappNote,
      href: waLink,
      highlight: true,
    },
    ...EXTRA_PHONES.map((p) => ({
      key: `phone-${p.key}`,
      icon: Phone,
      label: `${t.contact.phoneLabel} — ${p.role[lang]}`,
      value: p.display,
      note: t.contact.phoneNote,
      href: `https://wa.me/${p.number}?text=${encodeURIComponent(t.waMessage)}`,
      highlight: false,
    })),
    {
      key: "email",
      icon: Mail,
      label: t.contact.emailLabel,
      value: EMAIL,
      note: null,
      href: `mailto:${EMAIL}`,
      highlight: false,
    },
    {
      key: "instagram",
      icon: Instagram,
      label: t.contact.instagramLabel,
      value: INSTAGRAM_HANDLE,
      note: t.contact.instagramNote,
      href: INSTAGRAM_URL,
      highlight: false,
    },
    {
      key: "address",
      icon: MapPin,
      label: t.contact.addressLabel,
      value: ADDRESS,
      note: t.contact.addressNote,
      href: MAPS_URL,
      highlight: false,
    },
  ];

  return (
    <section id="kontak" className="border-t border-border bg-secondary/40 py-24 dark:bg-secondary/20 md:py-36" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-10 lg:grid-cols-2 lg:gap-10">
        <div>
          <SectionHead overline={t.contact.overline} title={t.contact.title} sub={t.contact.sub} testId="contact-head" />
          <Reveal delay={0.2}>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-cta-whatsapp"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-primary px-8 py-4 font-display text-base font-bold text-primary-foreground transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl"
            >
              <MessageCircle className="h-5 w-5" />
              {t.contact.cta}
            </a>
          </Reveal>
        </div>

        <div className="space-y-4">
          {rows.map((row, i) => {
            const Icon = row.icon;
            return (
              <Reveal key={row.key} delay={i * 0.08}>
                <a
                  href={row.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-${row.key}`}
                  className={`group flex items-center gap-5 rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                    row.highlight ? "border-primary bg-card" : "border-border bg-card"
                  }`}
                >
                  <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${row.highlight ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{row.label}</p>
                    <p className="mt-1 font-display text-lg font-semibold tracking-tight md:text-xl">{row.value}</p>
                    {row.note && <p className="mt-0.5 text-sm text-muted-foreground">{row.note}</p>}
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-primary" />
                </a>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-16 md:mt-20">
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-primary">{t.contact.mapTitle}</p>
          <div className="overflow-hidden rounded-2xl border border-border shadow-xl" data-testid="contact-map">
            <iframe
              title={t.contact.mapTitle}
              src="https://maps.google.com/maps?q=-6.210992,107.029889&z=16&output=embed"
              className="h-[360px] w-full md:h-[420px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </Reveal>
    </section>
  );
};
